import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  Image,
  Animated,
  Easing,
} from "react-native";
import { useTranslation } from "react-i18next";
import AddNewClientView from "./AddNewClientView";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../_layout";
import Entypo from "@expo/vector-icons/Entypo";
import AppStore from "../../assets/AppStore.png";
import PlayStore from "../../assets/PlayStore.png";
import AppStoreEN from "../../assets/AppStoreEN.png";
import PlayStoreEN from "../../assets/PlayStoreEN.png";
import { AspectRatios } from "../../Resources/Constants";
import { Ionicons } from "@expo/vector-icons";
import NoTranslateText from "./NoTranslateText";

const GettingStartedView = ({ coach }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();

  const completionByStep = useMemo(
    () => ({
      1: true,
      2: !coach.onboardingShowBlock,
      3: !coach.onboardingShowAthleteApp,
      4: !coach.onboardingShowInviteClient,
    }),
    [
      coach.onboardingShowBlock,
      coach.onboardingShowAthleteApp,
      coach.onboardingShowInviteClient,
    ]
  );

  const incompleteSteps = useMemo(
    () => [2, 3, 4].filter((step) => !completionByStep[step]),
    [completionByStep]
  );

  const [showingAddClient, setShowingAddClient] = useState(false);
  const initialExpanded = [];
  for (let i = 1; i <= 4; i++) {
    let isCompleted = false;
    if (i === 1) {
      isCompleted = true; // Step 1 always completed
    } else if (i === 2) {
      isCompleted = !coach.onboardingShowBlock;
    } else if (i === 3) {
      isCompleted = !coach.onboardingShowAthleteApp;
    } else if (i === 4) {
      isCompleted = !coach.onboardingShowInviteClient;
    }
    if (!isCompleted) {
      initialExpanded.push(i);
      break;
    }
  }
  const [expandedSteps, setExpandedSteps] = useState(initialExpanded);

  const [measuredHeights, setMeasuredHeights] = useState({});
  const stepProgressByNumberRef = useRef({});
  const getStepProgress = (stepNumber, initialIsExpanded) => {
    if (!stepProgressByNumberRef.current[stepNumber]) {
      stepProgressByNumberRef.current[stepNumber] = new Animated.Value(
        initialIsExpanded ? 1 : 0
      );
    }
    return stepProgressByNumberRef.current[stepNumber];
  };

  const prevCompletionRef = useRef(null);
  const prevPanelOpenRef = useRef(coach.showingTutorialPanel);
  useEffect(() => {
    const currentCompletion = [
      completionByStep[1],
      completionByStep[2],
      completionByStep[3],
      completionByStep[4],
    ];

    const prevCompletion = prevCompletionRef.current;
    prevCompletionRef.current = currentCompletion;

    const wasPanelOpen = prevPanelOpenRef.current;
    const isPanelOpen = coach.showingTutorialPanel;
    prevPanelOpenRef.current = isPanelOpen;

    // Only auto-expand when the panel transitions from closed -> open.
    if (!wasPanelOpen && isPanelOpen) {
      if (expandedSteps.length === 0 && incompleteSteps.length > 0) {
        setExpandedSteps([incompleteSteps[0]]);
        return;
      }
    }

    if (!coach.showingTutorialPanel || !prevCompletion) return;

    // Detect steps that transitioned from incomplete -> complete.
    const newlyCompletedSteps = [2, 3, 4].filter(
      (step) => !prevCompletion[step - 1] && currentCompletion[step - 1]
    );
    if (newlyCompletedSteps.length === 0) return;

    const completedStep = newlyCompletedSteps[0];
    const isCompletedStepExpanded = expandedSteps.includes(completedStep);
    if (!isCompletedStepExpanded) return;

    // Find the next incomplete step after the completed one; otherwise the first remaining.
    const nextStep =
      incompleteSteps.find((step) => step > completedStep) ||
      incompleteSteps[0] ||
      null;

    setExpandedSteps((prev) => {
      const withoutCompleted = prev.filter((step) => step !== completedStep);
      if (!nextStep) return withoutCompleted;
      if (withoutCompleted.includes(nextStep)) return withoutCompleted;
      return [...withoutCompleted, nextStep];
    });
  }, [
    coach.showingTutorialPanel,
    completionByStep,
    expandedSteps,
    incompleteSteps,
  ]);

  const isOffline = coach.isOffline;

  const createBlockForClient = async () => {
    if (coach.activeClients.length === 0) {
      return console.error("No clients available.");
    }

    if (screenSize === "small") {
      coach.setProperty("showingTutorialPanel", false);
    }

    coach.setProperty("showingCoachView", "ClientDashboard");

    if (coach.onboardingShowBlock) {
      coach.setProperty("highlightedComponent", "AddRow");
    }

    const selectedClient =
      coach.activeClients.find(
        (client) => client.id === coach.selectedClientId
      ) ||
      coach.activeClients.find(
        (client) => !client.email.includes("efortcoach")
      ) ||
      coach.activeClients[0];

    if (!selectedClient) return;

    coach.setProperty("selectedClientId", selectedClient.id);
    await coach.fetchNewClient(selectedClient.id, t, i18n.language, true);
    selectedClient.setProperty("selectedTab", "planification-string");

    if (selectedClient.selectedBlock) {
      selectedClient.selectedBlock.setProperty("isShowingDashboard", false);
    } else if (selectedClient.sortedBlocks?.length > 0) {
      const firstBlock = selectedClient.sortedBlocks[0];
      selectedClient.setSelectedBlock(firstBlock);
      firstBlock.setProperty("selectedWeek", firstBlock.getWeeks);
      firstBlock.setProperty("isShowingDashboard", false);
    }
  };

  const addNewClient = () => {
    (coach.headCoach || coach).setProperty(
      "showingCoachView",
      "CoachDashboard"
    );
    coach.setProperty("selectedClientId", null);
    setShowingAddClient(true);
  };

  const toggleExpandStep = (number) => {
    setExpandedSteps((prev) =>
      prev.includes(number)
        ? prev.filter((step) => step !== number)
        : [...prev, number]
    );
  };

  const TutorialStep = observer(({ number, text, expandedContent }) => {
    const [isHovered, setIsHovered] = useState(false);
    const isExpanded = expandedSteps.includes(number);
    const isCompleted = completionByStep[number];

    const progress = getStepProgress(number, isExpanded);
    const measuredHeight = measuredHeights[number] || 0;

    useEffect(() => {
      Animated.timing(progress, {
        toValue: isExpanded ? 1 : 0,
        duration: 300,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
    }, [isExpanded, progress]);

    const animatedHeight = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, measuredHeight],
    });

    const animatedOpacity = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    });

    const animatedMarginBottom = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 12],
    });

    const animatedMarginHorizontal = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 12],
    });

    return (
      <View
        style={[
          styles.stepContainer,
          {
            marginBottom: number === 4 ? 0 : 16,
            backgroundColor: "rgba(248, 250, 250, 1)",
            borderColor: Colors.borderColor,
          },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.stepButton,
            {
              backgroundColor:
                isHovered && !isExpanded && !isCompleted
                  ? "rgba(63, 123, 141, 0.1)"
                  : "rgba(248, 250, 250, 0)",
            },
          ]}
          onPress={() => toggleExpandStep(number)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View style={styles.stepRow}>
            <View
              style={[
                styles.numberContainer,
                isCompleted && styles.completedStep,
              ]}
            >
              {/* <Text
                style={[
                  styles.checkMark,
                  isCompleted && { color: Colors.whiteColor },
                ]}
              >
                {isCompleted ? "✓" : ""}
              </Text> */}
              {isCompleted && (
                <Ionicons
                  name="checkmark"
                  size={15}
                  color={Colors.whiteColor}
                />
              )}
            </View>
            <Text
              style={[
                Fonts.medium14,
                styles.text,
                { fontWeight: isHovered && !isCompleted ? "700" : "600" },
                isCompleted && {
                  textDecorationLine: "line-through",
                  color: "gray",
                },
              ]}
            >
              {t(text)}
            </Text>
          </View>
          <Entypo
            name={isExpanded ? "chevron-small-up" : "chevron-small-down"}
            size={24}
            color="black"
          />
        </TouchableOpacity>
        <Animated.View
          style={{
            height: animatedHeight,
            opacity: animatedOpacity,
            overflow: "hidden",
            marginBottom: animatedMarginBottom,
            marginHorizontal: animatedMarginHorizontal,
          }}
        >
          <View
            style={{ position: "absolute", left: 0, right: 0, top: 0 }}
            onLayout={(e) => {
              const nextHeight = e?.nativeEvent?.layout?.height || 0;
              if (!nextHeight) return;
              setMeasuredHeights((prev) =>
                prev[number] === nextHeight
                  ? prev
                  : { ...prev, [number]: nextHeight }
              );
            }}
          >
            {expandedContent}
          </View>
        </Animated.View>
      </View>
    );
  });

  const ImageButton = ({ isApple }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        onPress={() => coach.setProperty("showingAthleteAppOnboarding", true)}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Image
          source={
            i18n.language === "es"
              ? isApple
                ? AppStore
                : PlayStore
              : isApple
              ? AppStoreEN
              : PlayStoreEN
          }
          style={[
            styles.storeButton,
            {
              paddingLeft: 0,
              marginLeft: isApple ? 0 : 12,
              opacity: isHovered ? 0.7 : 1,
              width: isApple
                ? i18n.language === "es"
                  ? 38 * AspectRatios.AppStore
                  : 38 * AspectRatios.AppStoreEN
                : 38 * AspectRatios.PlayStore,
            },
          ]}
        />
      </TouchableOpacity>
    );
  };

  const TutorialStepButton = ({ buttonType }) => {
    const [isHovered, setIsHovered] = useState(false);
    const handlePress = () => {
      if (buttonType === "block") {
        createBlockForClient();
      } else if (buttonType === "client") {
        addNewClient();
      } else if (buttonType === "app") {
        coach.setProperty("showingAthleteAppOnboarding", true);
      }
    };

    const getText = () => {
      if (buttonType === "block") {
        return t("tutorialStep3text2-string");
      } else if (buttonType === "client") {
        return t("tutorialStep4text2-string");
      } else if (buttonType === "app") {
        return t("tutorialStep2text2-string");
      }
      return "";
    };

    return (
      <TouchableOpacity
        style={[
          styles.actionButton,
          { backgroundColor: Colors.bubbleColor, opacity: isHovered ? 0.7 : 1 },
        ]}
        onPress={coach.isOffline ? undefined : handlePress}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text style={{ color: Colors.whiteColor }}>{getText()}</Text>
      </TouchableOpacity>
    );
  };

  return (
    <View>
      <View style={styles.contentContainer}>
        <TutorialStep
          number={1}
          text="tutorialStep1-string"
          expandedContent={
            <>
              <NoTranslateText style={styles.expandedText}>
                {t("tutorialStep1text1-string")}
              </NoTranslateText>
            </>
          }
        />
        <TutorialStep
          number={2}
          text="tutorialStep3-string"
          expandedContent={
            <>
              <Text style={styles.expandedText}>
                {t("tutorialStep3text1-string")}
              </Text>
              <View style={styles.buttonRow}>
                <TutorialStepButton buttonType="block" />
              </View>
            </>
          }
        />
        <TutorialStep
          number={3}
          text="tutorialStep2-string"
          expandedContent={
            <View>
              <Text style={styles.expandedText}>
                {t("tutorialStep2text1-string")}
              </Text>
              <View style={styles.buttonRow}>
                <TutorialStepButton buttonType="app" />
              </View>
            </View>
          }
        />
        <TutorialStep
          number={4}
          text="tutorialStep4-string"
          expandedContent={
            <>
              <Text style={styles.expandedText}>
                {t("tutorialStep4text1-string")}
              </Text>
              <View style={styles.buttonRow}>
                <TutorialStepButton buttonType="client" />
              </View>
            </>
          }
        />
      </View>
      {showingAddClient && (
        <AddNewClientView
          isVisible={showingAddClient}
          onClose={() => setShowingAddClient(false)}
          coach={coach}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: "flex-start",
    justifyContent: "center",
    flex: 1,
  },
  stepContainer: {
    width: "100%",
    borderRadius: 10,
    borderWidth: 0.2,
    borderColor: "rgba(0, 0, 0, 0.05)",
    backgroundColor: "rgba(248, 250, 250, 1)",
  },
  stepButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    width: "100%",
    paddingHorizontal: 12,
    borderRadius: 10,
  },
  stepRow: { flexDirection: "row", alignItems: "center", flex: 1 },
  numberContainer: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.2)",
  },
  completedStep: {
    backgroundColor: "#2E5E6E",
    borderColor: "#2E5E6E",
  },
  checkMark: { fontSize: 14, color: Colors.whiteColor, fontWeight: "600" },
  text: { fontSize: 14, color: Colors.blackColor, marginLeft: 8 },
  expandedContent: { marginBottom: 12, marginHorizontal: 12 },
  expandedText: { marginBottom: 8 },
  storeButtonsRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 12,
    alignSelf: "center",
    maxWidth: "100%",
  },
  storeButton: { height: 38, resizeMode: "contain" },
  buttonRow: { alignItems: "center", marginTop: 12 },
  actionButton: {
    height: 30,
    borderRadius: 12,
    flexDirection: "row",
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    paddingHorizontal: 10,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default GettingStartedView;
